import React from 'react';
import { useFormContext } from 'react-hook-form';
import ActionCard from './ActionCard';
import { EqualisationFormValues } from './FormWrapper';
import { CurrencyCode } from 'further-types/lp/vehicle';
import EqualisationFooter from '../EqualisationFooter';
import { useParams } from 'react-router-dom';
import { useLayoutContext } from 'contexts/LayoutContext';
import { useEqualisationSection } from '../EqualisationContext';
import api from 'lib/trpcClient';
import { EqualisationSection } from 'further-types/lp/equalisation';
import EqualisationsTableHeader from 'components/EqualisationElements/EqualisationsTableHeader';
import allocationsAreValid from './helpers/allocationsAreValid';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { getQueryKey } from '@trpc/react-query';

interface EqualisationContentProps {
  baseCurrency: CurrencyCode;
}

const EqualisationContent: React.FC<EqualisationContentProps> = ({
  baseCurrency,
}) => {
  const { getValues, watch, reset } = useFormContext<EqualisationFormValues>();
  const {
    previousSection,
    nextSection,
    currentSection,
    goToNextSection,
    goToPreviousSection,
  } = useEqualisationSection();
  const { id } = useParams<{ id?: string }>();
  const { selectedVehicleId } = useLayoutContext();

  const equalisationActionAllocationsQueryKey = getQueryKey(
    api.equalisations.getEqualisationActionAllocations,
    {
      equalisationSection: currentSection,
      vehicleId: selectedVehicleId ?? '',
      equalisationId: id ?? '',
    },
  );

  const updateEqualisation = api.equalisations.update.useMutation(
    useHandleTrpcMutation()({
      invalidations: [equalisationActionAllocationsQueryKey],
    }),
  );

  const allocationBreakdownIsValid = watch('actions').every((action) =>
    allocationsAreValid(action),
  );

  const handleNext = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: nextSection ?? EqualisationSection.Summary,
        actions: getValues('actions'),
      },
      {
        onSuccess: () => {
          reset(getValues());
          goToNextSection();
        },
      },
    );
  };

  const handlePrevious = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: previousSection ?? EqualisationSection.Commitments,
        actions: getValues('actions'),
      },
      {
        onSuccess: () => {
          reset(getValues());
          goToPreviousSection();
        },
      },
    );
  };

  return (
    <>
      <EqualisationsTableHeader
        headers={['Counterparty', 'Type', 'Date', 'Amount']}
        gridTemplateColumns="40% 20% 19% 19%"
      />
      {watch('actions')?.map((action, index) => (
        <ActionCard
          key={index}
          actionIndex={index}
          isLastElement={index === watch('actions')?.length - 1}
          action={action}
          baseCurrency={baseCurrency}
        />
      ))}
      <EqualisationFooter
        handleNext={handleNext}
        handlePrevious={handlePrevious}
        disabled={!allocationBreakdownIsValid}
        loading={updateEqualisation.isPending}
      />
    </>
  );
};

export default EqualisationContent;
